/** @prettier */
import {TFunction} from 'i18next';
import {barLineMixedGraphBasicSetting} from './barLineMixedGraphBasicSetting';
import {graph} from '../../../../define';
import {GraphDataRange} from '../../../../Type/TypeDef';

/**
 * 熱量グラフ Q-ton Chiller の表示設定
 * @param props props
 * @returns 表示設定
 */
export const aggregationQtonHyozanEnergyGraphSetting = (props: {
  xLabel: string[]; //X軸のラベル
  dateRange: GraphDataRange; //データ取得範囲
  csvFileNameDate: string; //CSVファイルの名前（日付部分）
  csvFileNameTarget: string; //CSVファイルの名前（サイトの名前 + グラフの対象の名前）
  energyGraphData: ApexAxisChartSeries['0']['data']; //熱量グラフデータ
  copGraphData: ApexAxisChartSeries['0']['data']; //COPグラフデータ
  comparisonEnergyGraphData: ApexAxisChartSeries['0']['data']; //熱量比較グラフデータ
  comparisonCopGraphData: ApexAxisChartSeries['0']['data']; //COP比較グラフデータ
  trans: TFunction; //翻訳関数
}) => {
  /** Apex Chartsのseries設定
   * - name グラフの凡例
   * - type グラフの種類
   * - group グラフデータのグループ
   * - data グラフデータ
   */
  let seriesProps: ApexAxisChartSeries = [];

  //COMPARISONボタンの押下毎に、表示するグラフと凡例の数を切り替える。
  if (
    props.comparisonEnergyGraphData.length > 0 ||
    props.comparisonCopGraphData.length > 0
  ) {
    //COMPARISONボタンの押下回数が奇数の場合、グラフと凡例を4つずつ表示する。
    //奇数:comparison**のデータをBarLineMixedChartGraphから受け取る。(データ長≠0)
    //偶数:comparison**のデータをBarLineMixedChartGraphから受け取らない。(データ長＝0)
    seriesProps = [
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.comparisonEnergyAmount'
        )}`,
        type: 'column',
        group: 'comparison',
        data: props.comparisonEnergyGraphData,
      },
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.comparisonCop'
        )}`,
        type: 'line',
        group: 'comparison',
        data: props.comparisonCopGraphData,
      },
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.energyAmount'
        )}`,
        type: 'column',
        group: 'normal',
        data: props.energyGraphData,
      },
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.cop'
        )}`,
        type: 'line',
        group: 'normal',
        data: props.copGraphData,
      },
    ];
  } else {
    seriesProps = [
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.energyAmount'
        )}`,
        type: 'column',
        data: props.energyGraphData,
      },
      {
        name: `${props.trans(
          'aggregationQtonHyozanBarLineMixedGraphSetting.graphItem.cop'
        )}`,
        type: 'line',
        data: props.copGraphData,
      },
    ];
  }

  //COPデータからCOPグラフのy軸の最大値を決定する
  /** COPグラフのy軸の最大値（y軸の最大値が1未満だとエラーになるため初期値は1） */
  let yaxisMax = 1;
  for (const cop of [...props.copGraphData, ...props.comparisonCopGraphData]) {
    if (typeof cop === 'number' && cop > yaxisMax) {
      yaxisMax = cop;
    }
  }

  /** Apex Chartsのoptions設定
   * - xLabel X軸に表示するラベル
   * - csvFileNameDate csvファイル名に表示する日付
   * - csvFileNameTarget csvファイル名に表示するサイト名 + ユニット名
   * - trans 翻訳関数
   */
  let optionProps = barLineMixedGraphBasicSetting({
    xLabel: props.xLabel,
    csvFileNameDate: props.csvFileNameDate,
    csvFileNameTarget: props.csvFileNameTarget,
    copYaxisMax: yaxisMax,
    trans: props.trans,
  });

  /** グラフの配色一覧 */
  const graphColor = [
    graph.GRAPH_COLOR_HEAT, //Heat 0 熱量データ
    graph.GRAPH_COLOR_PLUM, //Plum 1 COPデータ
    graph.GRAPH_COLOR_LIGHT_HEAT, //Light Heat 2 熱量データ（比較）
    graph.GRAPH_COLOR_LIGHT_PLUM, //Light Plum 3 COPデータ（比較）
  ];

  //COMPARISONボタンの押下毎に、グラフの配色を切り替える。
  if (
    props.comparisonEnergyGraphData.length > 0 ||
    props.comparisonCopGraphData.length > 0
  ) {
    //奇数:comparison**のデータをBarChartGraphから受け取る。(データ長≠0)
    //偶数:comparison**のデータをBarChartGraphから受け取らない。(データ長＝0)
    optionProps.colors = [
      graphColor[2],
      graphColor[3],
      graphColor[0],
      graphColor[1],
    ];
  } else {
    optionProps.colors = [graphColor[0], graphColor[1]];
  }

  //データ取得範囲が1日の場合や比較状態かつデータ取得範囲が1週間の場合、
  //ラベルの間隔が狭く見づらくなるため、x軸のラベルを傾けるオプションを追加する
  if (
    (props.dateRange === 'day' ||
      (props.dateRange === 'week' &&
        (props.comparisonCopGraphData.length > 0 ||
          props.comparisonEnergyGraphData.length > 0))) &&
    optionProps.xaxis &&
    optionProps.xaxis.labels
  ) {
    optionProps.xaxis.labels.rotate = -45;
    optionProps.xaxis.labels.rotateAlways = true;
  }
  return {
    series: seriesProps,
    options: optionProps,
  };
};
